import debounce from "debounce";

export class ConnectionError extends Error {}

export class Connection {
  readonly subdomain: string;
  private ws: WebSocket;
  private hbTimer: number;
  private closeCallback?: () => void;
  private messageCallback?: (data: any) => void;
  private constructor(
    domain: string,
    ws: WebSocket,
    closeCallback?: () => void,
    messageCallback?: (data: any) => void
  ) {
    this.subdomain = domain;
    this.closeCallback = closeCallback;
    this.messageCallback = messageCallback;
    this.ws = ws;
    ws.onmessage = this.handleIncomingRequest.bind(this);
    this.hbTimer = setInterval(this.heartbeat.bind(this), 5000);

    // Initialize server response from localStorage
    if (localStorage.getItem("response")) {
      try {
        const savedResponse = JSON.parse(
          localStorage.getItem("response") || ""
        );
        this.updateResponse(
          savedResponse["code"],
          savedResponse["headers"],
          savedResponse["body"]
        );
      } catch {
        localStorage.removeItem("response");
      }
    }
  }

  private heartbeat() {
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send("hb");
    } else {
      this.close();
    }
  }

  private handleIncomingRequest(message: MessageEvent) {
    if (message.data === "TIMEOUT") {
      this.close();
      return;
    }
    this.messageCallback && this.messageCallback(JSON.parse(message.data));
  }

  public updateResponse = debounce(
    (code: string, headers: [string, string][], body: string) => {
      this.ws.send(
        JSON.stringify({
          code: parseInt(code),
          headers: headers,
          body: body,
        })
      );
      // localStorage.setItem(
      //   "response",
      //   JSON.stringify({
      //     code: parseInt(code),
      //     headers: headers,
      //     body: body,
      //   })
      // );
    },
    1000,
    { immediate: true }
  );

  public close(callback: boolean = true) {
    clearInterval(this.hbTimer);
    this.ws.close();
    callback && this.closeCallback && this.closeCallback();
  }

  static async new(
    messageCallback?: (data: any) => void,
    closeCallback?: () => void,
    targetSubdomain?: string
  ): Promise<Connection> {
    const protocol = window.location.protocol.includes("https") ? "wss" : "ws";

    const ws = new WebSocket(
      `${protocol}://${
        import.meta.env.VITE_BACKEND_URL ?? window.location.host
      }/bin${targetSubdomain ? `?subdomain=${targetSubdomain}` : ""}`
    );

    let resolve: (value: Connection | PromiseLike<Connection>) => void;
    let reject: (reason?: any) => void;
    const deferred = new Promise<Connection>((_resolve, _reject) => {
      resolve = _resolve;
      reject = _reject;
    });

    const timeout = setTimeout(() => {
      ws.close();
      reject(new ConnectionError("Connection timed out."));
    }, 5000);

    ws.onmessage = (message: MessageEvent) => {
      clearTimeout(timeout);
      if (message.data === "BAD_REQUEST") {
        ws.close();
        reject(
          new ConnectionError(
            "Invalid subdomain requested - must be exactly 8 lowercase alphanumeric characters."
          )
        );
      } else if (message.data === "CONFLICT") {
        ws.close();
        reject(new ConnectionError("Subdomain already in use."));
      } else {
        resolve(
          new Connection(message.data, ws, closeCallback, messageCallback)
        );
      }
    };

    return deferred;
  }
}
